import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Tag from '../ui/Tag';
import { calculateOvertimeHours, compareCalculationMethods } from '../../core/TimeCalculator';
import './OvertimeForm.css';

const OvertimeForm = ({ onSubmit, onCancel, initialData = null, className = '' }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    startTime: initialData?.startTime || '',
    endTime: initialData?.endTime || ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // 处理输入变化
  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // 清除相关错误
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // 实时预览计算结果
    if (newFormData.startTime && newFormData.endTime) {
      try {
        const comparison = compareCalculationMethods(newFormData.startTime, newFormData.endTime);
        setPreviewData(comparison);
      } catch (error) {
        setPreviewData(null);
      }
    } else {
      setPreviewData(null);
    }
  };

  // 验证表单
  const validateForm = () => {
    const newErrors = {};

    if (!formData.startTime) {
      newErrors.startTime = t('overtime.validation.startTimeRequired');
    }

    if (!formData.endTime) {
      newErrors.endTime = t('overtime.validation.endTimeRequired');
    }

    if (formData.startTime && formData.endTime) {
      const startTime = new Date(formData.startTime);
      const endTime = new Date(formData.endTime);

      if (endTime <= startTime) {
        newErrors.endTime = t('overtime.validation.endTimeAfterStart');
      }

      const hours = calculateOvertimeHours(startTime, endTime);
      if (hours <= 0) {
        newErrors.endTime = '加班时间必须大于0小时';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 处理提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        startTime: formData.startTime,
        endTime: formData.endTime
      });
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 处理取消
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className={`overtime-form ${className}`}>
      <div className="overtime-form__header">
        <h3 className="overtime-form__title">
          {initialData ? t('overtime.edit') : t('overtime.record')}
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="overtime-form__form">
        {/* 开始时间 */}
        <div className="overtime-form__field">
          <Input
            type="datetime-local"
            label={t('overtime.startTime')}
            value={formData.startTime}
            onChange={(value) => handleInputChange('startTime', value)}
            error={errors.startTime}
            required
            className="overtime-form__input"
          />
        </div>

        {/* 结束时间 */}
        <div className="overtime-form__field">
          <Input
            type="datetime-local"
            label={t('overtime.endTime')}
            value={formData.endTime}
            onChange={(value) => handleInputChange('endTime', value)}
            error={errors.endTime}
            required
            className="overtime-form__input"
          />
        </div>

        {/* 实时预览 */}
        {previewData && (
          <div className="overtime-form__preview">
            <h4 className="overtime-form__preview-title">计算预览</h4>
            <div className="overtime-form__preview-content">
              <div className="overtime-form__preview-item">
                <span className="overtime-form__preview-label">时间段：</span>
                <Tag variant="time" size="sm">
                  {previewData.startTime} - {previewData.endTime}
                </Tag>
              </div>
              
              <div className="overtime-form__preview-item">
                <span className="overtime-form__preview-label">加班时间：</span>
                <Tag variant="result" size="sm">
                  {previewData.overtime.hours} 小时
                </Tag>
                <Tag variant="note" size="sm">
                  连续时间，包含午休
                </Tag>
              </div>
              
              <div className="overtime-form__preview-item">
                <span className="overtime-form__preview-label">如果调休：</span>
                <Tag variant="result" size="sm">
                  {previewData.working.hours} 小时
                </Tag>
                <Tag variant="note" size="sm">
                  工作时间，扣除午休
                </Tag>
              </div>
              
              {previewData.difference.hours > 0 && (
                <div className="overtime-form__preview-item">
                  <span className="overtime-form__preview-label">差异：</span>
                  <Tag variant="warning" size="sm">
                    +{previewData.difference.hours} 小时
                  </Tag>
                  <Tag variant="note" size="sm">
                    {previewData.difference.description}
                  </Tag>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 提交错误 */}
        {errors.submit && (
          <div className="overtime-form__error">
            {errors.submit}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="overtime-form__actions">
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {initialData ? t('common.save') : t('overtime.record')}
          </Button>
          
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OvertimeForm;
